/**
 * Hook: useMemberRoles
 *
 * Wrapper con React Query alrededor de las funciones del servicio
 * `memberRoles`. Expone consultas por member/persona/club y mutaciones para
 * asignar y desactivar cargos.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  assignRole,
  deactivateAssignment,
  getActiveAssignmentsByClub,
  getActiveAssignmentsByPerson,
  getActiveAssignmentsBySection,
  getAllAssignmentsByMember,
  hardDeleteAssignment,
  updateAssignment,
  type AssignRoleInput,
} from "@/lib/services/memberRoles";
import type { ClubType, MemberRole, MemberRoleAssignment } from "@/types";

/** Todas las asignaciones (activas + inactivas) de un member. */
export function useMemberRoles(memberId: string | undefined) {
  return useQuery<MemberRoleAssignment[]>({
    queryKey: ["memberRoles", "byMember", memberId ?? "__none__"],
    queryFn: () =>
      memberId ? getAllAssignmentsByMember(memberId) : Promise.resolve([]),
    enabled: !!memberId,
    staleTime: 30_000,
  });
}

/** Asignaciones activas de una persona a lo largo de todos sus clubes. */
export function useActiveAssignmentsByPerson(personId: string | undefined) {
  return useQuery<MemberRoleAssignment[]>({
    queryKey: ["memberRoles", "byPerson", personId ?? "__none__"],
    queryFn: () =>
      personId ? getActiveAssignmentsByPerson(personId) : Promise.resolve([]),
    enabled: !!personId,
    staleTime: 30_000,
  });
}

/** Asignaciones activas de un club. */
export function useActiveAssignmentsByClub(clubId: string | undefined) {
  return useQuery<MemberRoleAssignment[]>({
    queryKey: ["memberRoles", "byClub", clubId ?? "__none__"],
    queryFn: () =>
      clubId ? getActiveAssignmentsByClub(clubId) : Promise.resolve([]),
    enabled: !!clubId,
    staleTime: 30_000,
  });
}

/** Asignaciones activas por sección dentro de un club. */
export function useActiveAssignmentsBySection(
  clubId: string | undefined,
  section: ClubType | undefined,
) {
  return useQuery<MemberRoleAssignment[]>({
    queryKey: [
      "memberRoles",
      "bySection",
      clubId ?? "__none__",
      section ?? "__none__",
    ],
    queryFn: () =>
      clubId && section
        ? getActiveAssignmentsBySection(clubId, section)
        : Promise.resolve([]),
    enabled: !!clubId && !!section,
    staleTime: 30_000,
  });
}

/**
 * Mutación: asignar cargo. Invalida las queries relacionadas para que la UI
 * se refresque sola.
 */
export function useAssignRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignRoleInput) => assignRole(input),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({ queryKey: ["memberRoles"] });
      qc.invalidateQueries({ queryKey: ["members", "byId", vars.memberId] });
      qc.invalidateQueries({ queryKey: ["members", "byClub", vars.clubId] });
    },
  });
}

/**
 * Mutación: desactivar cargo (marca status='inactive').
 */
export function useDeactivateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      assignmentId: string;
      userId: string;
      userName?: string;
      reason?: string;
    }) =>
      deactivateAssignment(input.assignmentId, {
        userId: input.userId,
        userName: input.userName,
        reason: input.reason,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["memberRoles"] });
      qc.invalidateQueries({ queryKey: ["members"] });
    },
  });
}

/** Mutación: editar rol/sección de una asignación existente. */
export function useUpdateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      assignmentId: string;
      role?: MemberRole;
      section?: ClubType;
      notes?: string;
      userId: string;
      userName?: string;
    }) =>
      updateAssignment(
        input.assignmentId,
        { role: input.role, section: input.section, notes: input.notes },
        { userId: input.userId, userName: input.userName },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["memberRoles"] });
      qc.invalidateQueries({ queryKey: ["members"] });
    },
  });
}

/** Mutación: eliminar definitivamente una asignación (corrección de error). */
export function useHardDeleteAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { assignmentId: string; userId: string }) =>
      hardDeleteAssignment(input.assignmentId, { userId: input.userId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["memberRoles"] });
      qc.invalidateQueries({ queryKey: ["members"] });
    },
  });
}
